import React, { useEffect, useRef, useState, useMemo } from "react";
import SearchLayout from "../../layouts/SearchLayout";
import { useDispatch } from "react-redux";
import { homeActions } from "../../store/store";
import {
  Configure,
  Hits,
  Stats,
  CurrentRefinements,
  ClearRefinements,
  InstantSearch,
  usePagination,
} from "react-instantsearch";
import Hit from "./Hit/Hit";
import CustomSortBy from "../../hooks/customSortBy";
import RefinementListCard from "./RefinementListCard/RefinementListCard";
import CustomPagination from "../../hooks/customPagination";
import { getIndexSize } from "../../services/search-services";
import ResultsSkeleton from "./ResultsSkeleton";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuthContext } from "@asgardeo/auth-react";
import { refreshAccessTokenOnce } from "../../services/auth-token-refresh";
import favImage from "../../assets/images/fav.webp";
import { Helmet } from "react-helmet-async";
import { searchClient } from "../../services/typesense-client";
import { safeRouting } from "../../services/instantsearch-routing";
import { useSelector } from "react-redux";

const PRESIGNED_BASE_URL = import.meta.env.VITE_PRESIGNED_BASE_URL;

function PendingSearchRedirect({ shouldRestorePage }) {
  const { currentRefinement, nbPages, refine } = usePagination();

  useEffect(() => {
    if (!shouldRestorePage || nbPages <= 0) {
      return;
    }

    const pendingSearchPage = sessionStorage.getItem("pendingSearchPage");

    if (pendingSearchPage === null) {
      return;
    }

    const targetPage = Number.parseInt(pendingSearchPage, 10);

    if (Number.isNaN(targetPage) || targetPage < 0) {
      sessionStorage.removeItem("pendingSearchPage");
      return;
    }

    if (targetPage !== currentRefinement) {
      refine(targetPage);
    }

    sessionStorage.removeItem("pendingSearchPage");
  }, [currentRefinement, nbPages, refine, shouldRestorePage]);

  return null;
}

const Results = () => {
  const selectedOption = useSelector((state) => state.home.selectedOption);
  const { state, getAccessToken, refreshAccessToken } = useAuthContext();
  const [userToken, setUserToken] = useState("noToken");
  const dispatch = useDispatch();
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);
  const [isLoading, setIsLoading] = useState(true);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [indexSize, setIndexSize] = useState(0);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const hiddenLayer = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  //const { state } = useAuthContext();
  const searchType = sessionStorage.getItem("searchType");

  let queryBy;
  let inFix;
  // Default to relevance. Only apply sort when user selects via SortBy widget.
  let sortBy = undefined;

  useEffect(() => {
    const fetchToken = async () => {
      if (state.isAuthenticated) {
        const accessToken = await getAccessToken();
        setUserToken(accessToken);
      } else {
        setUserToken("noToken");
      }
    };
    fetchToken();
  }, [state.isAuthenticated, getAccessToken]);

  if (searchType === "Exact Match") {
    queryBy = "decision_text";
    inFix = "off";
  } else if (searchType === "Semantic Match") {
    queryBy = "summary_vec";
    inFix = "off";
  } else if (searchType === "Case Number") {
    queryBy = "standard_casenumber";
    inFix = "always";
  } else if (searchType === "Parties") {
    queryBy = "nameofparties";
    inFix = "off";
  } else {
    queryBy = "decision_text,summary_vec";
    inFix = "off";
  }

  // Keep a stable reference for searchClient to avoid extra searches
  const SearchClient = useMemo(
    () =>
      searchClient(queryBy, inFix, userToken, undefined, sortBy, {
        isAuthenticated: state.isAuthenticated,
        getAccessToken,
        refreshAccessToken,
      }),
    [
      getAccessToken,
      queryBy,
      inFix,
      refreshAccessToken,
      sortBy,
      state.isAuthenticated,
      userToken,
    ]
  );
  const PlaceHolder = isDesktop
    ? "Start typing a legal query, key word, party name, or case number"
    : "Type a legal query or key word";

  const openPDFWithPreSignedUrl = async (originalLink) => {
    const newWindow = window.open("about:blank", "_blank");

    try {
      const request = (accessToken) =>
        fetch(`${PRESIGNED_BASE_URL}/api/pdf/get-pdf-url`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
          body: JSON.stringify({ link: originalLink }),
        });

      let accessToken = state.isAuthenticated
        ? (await getAccessToken()) || ""
        : "";
      let response = await request(accessToken);
      if (response.status === 401 && state.isAuthenticated) {
        await refreshAccessTokenOnce(refreshAccessToken);
        accessToken = (await getAccessToken()) || "";
        response = await request(accessToken);
      }

      const data = await response.json();
      if (data.success && newWindow) {
        newWindow.location.href = data.presigned_url;
        return;
      }

      if (newWindow) {
        newWindow.location.href = originalLink;
      }
    } catch (error) {
      console.error("Error fetching pre-signed URL:", error);
      if (newWindow) {
        newWindow.location.href = originalLink;
      }
    }
  };

  useEffect(() => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "page_view",
      page: "Results Page",
      path: window.location.pathname,
    });
  }, []);

  // One-time setup: fetch index size, set page
  useEffect(() => {
    dispatch(homeActions.setCurrentPage("search"));

    const fetchData = async () => {
      const fetchedSize = await getIndexSize();
      setIndexSize(parseInt(fetchedSize));
      if (fetchedSize !== null) {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [dispatch]);

  // Post-login redirect: reacts to auth state instead of a fixed timeout
  useEffect(() => {
    // Wait until Asgardeo has finished initializing
    if (state.isLoading) return;

    const urlString = sessionStorage.getItem("urlString");
    const redirectAfterLogin = sessionStorage.getItem("redirectAfterLogin");
    const redirectToDocument = sessionStorage.getItem("redirectToDocument");

    // Redirect with search query params (e.g. user was on /search?q=...)
    if (urlString) {
      sessionStorage.removeItem("urlString");
      sessionStorage.removeItem("redirectAfterLogin");
      sessionStorage.removeItem("isRedirected");
      const newUrl = `/search${urlString.toString()}`;
      window.dataLayer.push({
        event: "search",
        event_label: "Redirect_to_search",
        url: newUrl,
      });
      window.location.href = newUrl;
      return;
    }

    // Always honor an explicit return path after login. For summary pages with
    // a pending document open, CaseSummary will complete the final PDF open.
    if (
      redirectAfterLogin &&
      state.isAuthenticated &&
      redirectAfterLogin !== location.pathname
    ) {
      if (!redirectToDocument) {
        sessionStorage.removeItem("redirectAfterLogin");
      }
      sessionStorage.removeItem("isRedirected");
      setIsRedirecting(false);
      navigate(redirectAfterLogin);
      return;
    }

    // No pending redirect — clear stale flags and show the page
    if (!redirectAfterLogin && !redirectToDocument) {
      sessionStorage.removeItem("isRedirected");
    }
    setIsRedirecting(false);
  }, [location.pathname, navigate, state.isAuthenticated, state.isLoading]);

  // Show redirecting state while auth is loading and there's a pending redirect
  useEffect(() => {
    if (state.isLoading) {
      const redirectAfterLogin = sessionStorage.getItem("redirectAfterLogin");
      const urlString = sessionStorage.getItem("urlString");
      if (redirectAfterLogin || urlString) {
        setIsRedirecting(true);
      }
    }
  }, [state.isLoading]);

  useEffect(() => {
    if (state.isLoading || !state.isAuthenticated || location.pathname !== "/search") {
      return;
    }

    const redirectToDocument = sessionStorage.getItem("redirectToDocument");
    const redirectAfterLogin = sessionStorage.getItem("redirectAfterLogin");

    if (!redirectToDocument || redirectAfterLogin) {
      return;
    }

    openPDFWithPreSignedUrl(redirectToDocument);
    sessionStorage.removeItem("redirectToDocument");
  }, [location.pathname, state.isAuthenticated, state.isLoading]);

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const toggleFilters = () => {
    setIsFilterOpen((prev) => !prev);
  };

  // Don't render InstantSearch until we know whether a redirect is needed.
  // Mounting it with a partially-valid URL causes routeToState to crash.
  if (isRedirecting || state.isLoading) {
    return (
      <div
        id="loading"
        className="flex flex-col items-center justify-center h-screen relative bg-pl-bg"
      >
        <div
          className="relative w-[150px] h-[150px] bg-center bg-no-repeat bg-contain"
          style={{ backgroundImage: `url(${favImage})` }}
        >
          <div className="absolute w-[14px] h-[14px] rounded-full animate-pulse-color top-[47px] left-[34px]"></div>
          <div className="absolute w-[14px] h-[14px] rounded-full animate-pulse-color top-[84px] left-[34px]"></div>
        </div>
        <div>
          <p
            id="loading-text"
            className="mt-4 text-center text-pl-accent"
          >
            {isRedirecting ? "Wait, we're redirecting you" : "Loading..."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <InstantSearch
      indexName={import.meta.env.VITE_TYPESENSE_COLLECTION_NAME}
      searchClient={SearchClient}
      routing={safeRouting}
      future={{
        preserveSharedStateOnUnmount: true,
      }}
    >
      <div className="relative">
        <Helmet>
          <title>Search | paralegal.lk</title>
          <meta
            name="description"
            content="Explore the vast collection of Sri Lankan case law with paralegal.lk. Search and find the most relevant legal decisions for your research."
          />
        </Helmet>
        <div
          ref={hiddenLayer}
          className={`fixed inset-0 bg-black bg-opacity-30 dark:bg-opacity-50 z-[140] duration-300 ${
            isFilterOpen ? "block opacity-100" : "hidden opacity-0"
          }`}
          onClick={toggleFilters}
        ></div>

        <SearchLayout
          isResults={true}
          showTab={true}
          showAlphabet={false}
          PlaceHolder={PlaceHolder}
          enableFiltering={true}
          enableSearchTracking={true}
          enableSuggestions={true}
          SearchAsYouType={false}
          showSearch={true}
          searchAnimation={false}
          placeholderWords={[]}
        >
          <div className="w-full">
            {isLoading ? (
              <ResultsSkeleton />
            ) : (
              <div className="flex flex-col py-20 pt-[150px] md:pt-[130px] max-w-6xl w-full px-3 md:px-0">
                <div className="px-10 mb-8 md:px-20 flex flex-nowrap w-full overflow-x-auto py-1">
                  <CurrentRefinements
                    classNames={{
                      root: "flex whitespace-nowrap",
                      noRefinementRoot: "",
                      noRefinementList: "",
                      item: "",
                      label: "hidden",
                      category:
                        "mx-0.5 rounded-full py-0.5 px-2 text-13 transition-all text-pl-text-primary bg-pl-bg-elevated border border-pl-border",
                      categoryLabel: "me-1",
                      list: "flex flex-nowrap",
                    }}
                  />
                  <ClearRefinements
                    translations={{
                      resetButtonText: "Clear all",
                    }}
                    classNames={{
                      root: "",
                      button:
                        "whitespace-nowrap mx-0.5 rounded-full py-0.5 px-2 text-13 transition-all text-pl-text-primary bg-pl-bg-subtle border border-pl-border",
                      disabledButton: "hidden",
                    }}
                  />
                </div>

                <Configure hitsPerPage={15} />
                <PendingSearchRedirect
                  shouldRestorePage={
                    state.isAuthenticated && location.pathname === "/search"
                  }
                />
                <div className="flex w-full md:gap-50 text-12 md:text-14 items-center ">
                  <div className="flex flex-grow w-3/4 px-[18px] md:px-6 text-pl-text-tertiary">
                    <Stats
                      translations={{
                        rootElementText(nbHits) {
                          let statsText = "";
                          if (nbHits.nbHits === 0) {
                            statsText = "No results";
                          } else if (nbHits.nbHits === 1) {
                            statsText = "1 result";
                          } else {
                            statsText = `${nbHits.nbHits} results`;
                          }
                          return `${statsText.toLocaleString()} found ${
                            indexSize && nbHits.nbHits !== indexSize
                              ? ` - Searched ${indexSize.toLocaleString()} decisions`
                              : ""
                          } in ${nbHits.processingTimeMS} ms.`;
                        },
                      }}
                    />
                  </div>
                  <div className="flex justify-start items-center gap-10 mr-25 md:mr-0 sm:gap-30 sm:w-1/4">
                    <CustomSortBy indexName="paralegal" />
                    {!isDesktop && (
                      <button
                        className="md:hidden flex text-pl-accent"
                        type="button"
                        aria-controls="filters-section"
                        aria-expanded={isFilterOpen}
                        onClick={toggleFilters}
                      >
                        <svg
                          width="14"
                          height="18"
                          viewBox="0 0 14 18"
                          fill="currentColor"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <g>
                            <path d="M9.83232 16.1503H11.499V17.817H9.83232M6.49899 16.1503H8.16565V17.817H6.49899M3.16565 16.1503H4.83232V17.817H3.16565M9.83232 14.3836C9.86565 14.6336 9.78232 14.9003 9.59065 15.0753C9.26565 15.4003 8.74065 15.4003 8.41565 15.0753L5.07399 11.7336C4.88232 11.542 4.79899 11.2836 4.83232 11.042V6.77529L0.840653 1.66696C0.55732 1.30862 0.623986 0.783622 0.98232 0.500289C1.14065 0.383622 1.31565 0.316956 1.49899 0.316956H13.1657C13.349 0.316956 13.524 0.383622 13.6823 0.500289C14.0407 0.783622 14.1073 1.30862 13.824 1.66696L9.83232 6.77529V14.3836ZM3.19899 1.98362L6.49899 6.20029V10.8003L8.16565 12.467V6.19196L11.4657 1.98362H3.19899Z" />
                          </g>
                        </svg>
                      </button>
                    )}
                  </div>
                </div>

                <div className="flex w-full md:gap-50">
                  <div className="md:w-3/4 w-full flex flex-col justify-between h-full">
                    <div className="flex-1">
                      <Hits
                        hitComponent={Hit}
                        classNames={{
                          list: "space-y-0",
                          item: "!p-0 !border-0 !shadow-none",
                        }}
                      />
                    </div>
                    <div className="mt-50">
                      <CustomPagination />
                    </div>
                  </div>

                  {isDesktop && (
                    <div className="flex w-1/4 px-10">
                      <RefinementListCard />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {!isDesktop && (
            <div
              id="filters-section"
              className={`fixed top-0 pb-50 right-0 max-w-full h-full bg-pl-bg z-[150] shadow-lg transform transition-transform duration-300 border-l border-pl-border ${
                isFilterOpen ? "translate-x-0" : "translate-x-full"
              }`}
            >
              <div className="flex p-10 pr-20 justify-between items-center bg-pl-bg-elevated border-y border-pl-border">
                <div className="text-16 text-pl-text-secondary font-medium">
                  Filters
                </div>
                <button
                  className="text-12 px-15 py-0.5 rounded-25 bg-pl-accent text-white border border-pl-accent"
                  onClick={toggleFilters}
                >
                  Save
                </button>
              </div>
              <div className="h-full overflow-y-auto">
                <RefinementListCard isMobile={true} />
              </div>
            </div>
          )}
        </SearchLayout>
      </div>
    </InstantSearch>
  );
};

export default Results;
